import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";
import { requireAuth } from "../lib/authGate";
import { isMishnahSlug, isTalmudSlug } from "../lib/hebrewRef";

/**
 * Source Basket — "סל מקורות לשיעור".
 *
 * Per-user, server-persisted. Each authenticated user has exactly one
 * basket document in MongoDB; logged-out users see an empty basket
 * and the floating button is hidden upstream.
 *
 * Behaviour
 * ---------
 *   user === null              → items is always []  (no localStorage,
 *                                 no leak between accounts on a
 *                                 shared browser)
 *   user transitions to value  → fetch GET /api/me/basket, replace
 *                                 in-memory state
 *   user transitions to null   → wipe in-memory state (the DB row is
 *                                 untouched, so the basket returns
 *                                 on the next login)
 *   any item mutation          → debounced PUT /api/me/basket
 *
 * Data shape — each item is one of:
 *   { id, type: "verse",      book, chapter, verse,  he, en, book_he, source }
 *   { id, type: "commentary", book, chapter, verse,  commentator, commentator_he, he, en, source }
 *   { id, type: "chapter",    book, chapter, book_he, verse_count, source }
 *   { id, type: "note",       text }            ← notes are corpus-less
 *
 * `source` is one of "tanakh" | "mishnah" and is set automatically at
 * add() time so the Lesson Builder knows which commentator catalog to
 * use for each item (Tanakh commentators ≠ Mishnah commentators).
 * Legacy items without `source` are healed on hydrate.
 */

const BasketContext = createContext(null);

// Legacy localStorage key from the pre-DB implementation. We
// proactively delete it on first mount so the cross-user leak that
// motivated this refactor can't survive a stale browser.
const LEGACY_KEY = "tanakh_basket";

// Debounce window — short enough that quick add/remove sequences land
// on the server before the user navigates away, long enough that a
// rapid 5-item add doesn't generate 5 separate PUTs.
const SYNC_DEBOUNCE_MS = 600;

function genId() {
  return (
    Date.now().toString(36) +
    Math.random().toString(36).slice(2, 8)
  );
}

/**
 * Derive the source corpus tag ("tanakh" | "mishnah") for a basket item.
 *
 * For notes (which have no book) → "tanakh" by convention so downstream
 * code never has to special-case `undefined`. For verse/commentary/
 * chapter items, infer from the `book` slug via `isMishnahSlug`.
 *
 * Returns the existing `source` field unchanged if it's already set —
 * so re-tagging is idempotent and a server-stored value always wins.
 */
function deriveSource(item) {
  if (item.source === "tanakh" || item.source === "mishnah" || item.source === "talmud") {
    return item.source;
  }
  if (item.type === "note") return "tanakh";
  // Talmud check runs first because Sefaria's canonical Talmud tractate
  // slug ("Berakhot") is a bare name, while the Mishnah of the same
  // tractate carries an explicit "Mishnah " prefix — so checking the
  // talmud rule first is unambiguous.
  if (isTalmudSlug(item.book)) return "talmud";
  return isMishnahSlug(item.book) ? "mishnah" : "tanakh";
}

function signatureOf(item) {
  if (item.type === "verse") return `v:${item.book}:${item.chapter}:${item.verse}`;
  if (item.type === "commentary")
    return `c:${item.book}:${item.chapter}:${item.verse}:${item.commentator}`;
  if (item.type === "chapter") return `ch:${item.book}:${item.chapter}`;
  return `n:${item.id}`;
}

export function BasketProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  // `hydrated` flips to true after we've finished the initial GET for
  // the current user. We use it to suppress the debounced PUT during
  // hydration so loading the basket doesn't immediately write it back.
  const [hydrated, setHydrated] = useState(false);

  // Wipe the legacy localStorage basket on mount — exactly once per
  // page load. This is the migration step the user signed off on
  // (option ii: don't merge old anonymous baskets, just clear them).
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_KEY);
    } catch {
      /* private mode / quota — non-fatal */
    }
  }, []);

  // Load (or reset) the basket when the user identity changes.
  useEffect(() => {
    let cancelled = false;
    setHydrated(false);
    if (!user) {
      // Logout (or initial anonymous): the floating button reads
      // count and will hide itself. We do NOT call the API — there
      // is nothing to clear server-side, by user requirement.
      setItems([]);
      setHydrated(true);
      return;
    }
    api
      .getBasket()
      .then((res) => {
        if (cancelled) return;
        const fetched = Array.isArray(res?.items) ? res.items : [];
        // Heal missing ids so remove(id) always has a unique target; also
        // tag legacy items that pre-date the Mishnah corpus with a source
        // (inferred from `book`). Items already carrying a valid `source`
        // are left untouched so any server-side correction wins.
        const seen = new Set();
        const healed = fetched.map((p) => {
          const safeId = p?.id && !seen.has(p.id) ? p.id : genId();
          seen.add(safeId);
          return { ...p, id: safeId, source: deriveSource(p) };
        });
        setItems(healed);
      })
      .catch(() => {
        // Network error on load — leave the basket empty rather than
        // silently restoring stale data. The next mutation will try
        // to PUT and surface any deeper problem.
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setHydrated(true);
      });
    return () => { cancelled = true; };
  }, [user]);

  // Debounced sync — fires whenever `items` changes after hydration.
  // We capture the latest snapshot in a ref so the timer always sends
  // the freshest items even if the user keeps editing during the
  // debounce window.
  const latestItemsRef = useRef(items);
  latestItemsRef.current = items;
  useEffect(() => {
    if (!user || !hydrated) return;
    const handle = setTimeout(() => {
      api.saveBasket(latestItemsRef.current).catch(() => {
        // Sync failure is non-fatal for the in-memory experience;
        // the basket stays usable, and the next mutation retries.
      });
    }, SYNC_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [items, user, hydrated]);

  const add = useCallback((rawItem) => {
    // Anonymous users can't accumulate a basket because the backend
    // basket endpoint requires auth anyway. Trigger the global Hebrew
    // "you need to sign up" modal so the user gets a clear, branded
    // message instead of a silent no-op.
    if (!requireAuth("basket")) return;
    setItems((prev) => {
      // Every item carries its `source` tag from the moment it's added,
      // so the Lesson Builder can pick the right commentator catalog
      // (Tanakh vs Mishnah) per item without re-deriving later.
      const withId = {
        ...rawItem,
        id: rawItem.id || genId(),
        source: deriveSource(rawItem),
      };
      const sig = signatureOf(withId);
      if (prev.some((p) => signatureOf(p) === sig)) return prev;
      return [...prev, withId];
    });
  }, []);

  const remove = useCallback((idOrSig) => {
    setItems((prev) => {
      const byId = prev.filter((p) => p.id !== idOrSig);
      if (byId.length !== prev.length) return byId;
      return prev.filter((p) => signatureOf(p) !== idOrSig);
    });
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const move = useCallback((id, dir) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx < 0) return prev;
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }, []);

  const has = useCallback(
    (probe) =>
      items.some((p) => signatureOf(p) === signatureOf({ ...probe, id: probe.id || "x" })),
    [items]
  );

  const value = useMemo(
    () => ({ items, add, remove, clear, move, has, count: items.length }),
    [items, add, remove, clear, move, has]
  );

  return (
    <BasketContext.Provider value={value}>{children}</BasketContext.Provider>
  );
}

export function useBasket() {
  const ctx = useContext(BasketContext);
  if (!ctx) {
    return {
      items: [],
      add: () => {},
      remove: () => {},
      clear: () => {},
      move: () => {},
      has: () => false,
      count: 0,
    };
  }
  return ctx;
}
